'use client';

import { useState, useEffect, ReactNode } from 'react';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

export interface ListWithCardBlockProps {
  id?: string;
  title: ReactNode | SerializedEditorState;
  subtitle?: ReactNode | SerializedEditorState;
  list: Array<{
    title: ReactNode | SerializedEditorState;
    subtitle: ReactNode | SerializedEditorState;
  }>;
  card: {
    title: string;
    btnContent: string;
    price: string;
    priceSuffix: string;
  };
}

export default function ListWithCardBlock({
  id,
  title,
  subtitle,
  list,
  card,
}: ListWithCardBlockProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prevIndex => (prevIndex + 1) % list.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [list.length]);

  return (
    <div
      id={id}
      data-meta="ListWithCardBlock"
      className="container max-w-7xl mx-auto my-24 overflow-hidden"
    >
      <div className="px-4 py-5 sm:px-6 flex flex-col justify-center">
        <CustomRichText
          data={title}
          tag="div"
          className="text-4xl xl:text-6xl pb-4 text-center font-bold text-white"
        />
        {subtitle && (
          <CustomRichText
            data={subtitle}
            tag="div"
            className="mt-6 text-sm md:text-md lg:text-xl md:leading-6 lg:leading-8 text-white text-center"
          />
        )}
      </div>
      <div className="py-4 mb-8 rounded-[2rem] flex flex-col lg:grid lg:grid-cols-12 justify-between">
        <div className="col-span-12 lg:col-span-6">
          <div className="max-sm:px-4 mx-auto flex flex-col justify-between">
            {list.map((item, index) => (
              <div
                key={index}
                className={`vertical-divider flex justify-start ${
                  index === 0 ? `first` : ''
                } ${index === list.length - 1 ? 'last' : ''} ${
                  activeIndex === index ? 'white active' : ''
                }`}
              >
                <div className="flex text-white flex-col lg:items-start mb-6">
                  <CustomRichText
                    data={item.title}
                    tag="div"
                    className="text-xl font-bold text-white"
                  />
                  <CustomRichText
                    data={item.subtitle}
                    tag="div"
                    className="block text-sm text-white"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-6">
          <div className="flex flex-col bg-white rounded-3xl mt-8 p-4">
            <div className="ring-gray-200 rounded-3xl justify-between bg-white">
              <div className="flex items-center justify-between gap-x-4">
                <p className="uppercase text-white rounded-full py-1 px-2 custom-gradient-green text-base font-semibold leading-8">
                  {card.title}
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col items-baseline gap-x-1">
              <p>
                <span className="text-4xl font-bold tracking-tight text-gray-900">
                  {card.price}
                </span>
                <span className="text-xl font-semibold leading-6 text-gray-600">
                  {card.priceSuffix}
                </span>
              </p>
              <span className="text-sm leading-5 text-gray-400">{card.btnContent}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
